import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const Layoff = () => {
    const [layoffs, setLayoffs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [showModal, setShowModal] = useState(false);
    const [editingLayoff, setEditingLayoff] = useState(null);
    const [employees, setEmployees] = useState([]);
    const [filteredEmployees, setFilteredEmployees] = useState([]);
    const [showEmployeeList, setShowEmployeeList] = useState(false);
    const [formData, setFormData] = useState({
        emp_id: '',
        employee_search: '',
        from_date: '',
        to_date: '',
        reason: '',
        att_value: ''
    });
    const [userPermissions, setUserPermissions] = useState({
        can_view: false,
        can_add: false,
        can_edit: false,
        can_delete: false,
        can_export: false,
        can_import: false,
        loaded: false
    });

    useAuth();
    const navigate = useNavigate();
    const API_BASE = process.env.REACT_APP_API_BASE || 'http://localhost:8000/api';

    const fetchUserPermissions = useCallback(async () => {
        try {
            const token = localStorage.getItem('authToken');
            if (!token) {
                setUserPermissions({
                    can_view: false,
                    can_add: false,
                    can_edit: false,
                    can_delete: false,
                    can_export: false,
                    can_import: false,
                    loaded: true
                });
                return;
            }

            const response = await axios.get(`${API_BASE}/get_user_permissions.php`, {
                headers: { Authorization: `Bearer ${token}` }
            });

            if (response.data.success && response.data.permissions) {
                const layoffPermissions = response.data.permissions.find(
                    p => p.module_name === 'Layoff' || p.module_name === 'Layoff Register'
                );

                if (layoffPermissions) {
                    setUserPermissions({
                        can_view: Boolean(layoffPermissions.can_view),
                        can_add: Boolean(layoffPermissions.can_add),
                        can_edit: Boolean(layoffPermissions.can_edit),
                        can_delete: Boolean(layoffPermissions.can_delete),
                        can_export: Boolean(layoffPermissions.can_export),
                        can_import: Boolean(layoffPermissions.can_import || false),
                        loaded: true
                    });
                } else {
                    setUserPermissions({
                        can_view: false,
                        can_add: false,
                        can_edit: false,
                        can_delete: false,
                        can_export: false,
                        can_import: false,
                        loaded: true
                    });
                }
            } else {
                setUserPermissions({
                    can_view: false,
                    can_add: false,
                    can_edit: false,
                    can_delete: false,
                    can_export: false,
                    can_import: false,
                    loaded: true
                });
            }
        } catch (error) {
            console.error('Error fetching permissions:', error);
            setUserPermissions({
                can_view: false,
                can_add: false,
                can_edit: false,
                can_delete: false,
                can_export: false,
                can_import: false,
                loaded: true
            });
        }
    }, [API_BASE]);

    const fetchEmployees = useCallback(async () => {
        try {
            const token = localStorage.getItem('authToken');
            const response = await axios.get(`${API_BASE}/layoff/get_employees.php`, {
                headers: { Authorization: `Bearer ${token}` }
            });

            if (response.data.success) {
                setEmployees(response.data.employees);
                setFilteredEmployees(response.data.employees);
            }
        } catch (error) {
            console.error('Error fetching employees:', error);
        }
    }, [API_BASE]);

    const fetchLayoffs = useCallback(async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem('authToken');
            const response = await axios.get(`${API_BASE}/layoff/get_layoffs.php`, {
                headers: { Authorization: `Bearer ${token}` }
            });

            if (response.data.success) {
                setLayoffs(response.data.layoffs);
            }
        } catch (error) {
            setError('Failed to fetch layoff records');
            console.error('Error fetching layoffs:', error);
        } finally {
            setLoading(false);
        }
    }, [API_BASE]);

    useEffect(() => {
        fetchUserPermissions();
    }, [fetchUserPermissions]);

    useEffect(() => {
        if (userPermissions.loaded && userPermissions.can_view) {
            fetchEmployees();
            fetchLayoffs();
        }
    }, [userPermissions.loaded, userPermissions.can_view, fetchEmployees, fetchLayoffs]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;

        // If clearing the search box, also clear emp_id
        if (name === 'employee_search' && !value) {
            setFormData(prev => ({
                ...prev,
                [name]: value,
                emp_id: '' // Clear employee ID
            }));
        } else {
            setFormData(prev => ({
                ...prev,
                [name]: value
            }));
        }

        // Handle employee search
        if (name === 'employee_search') {
            if (value.length > 1) {
                const filtered = employees.filter(emp =>
                    `${emp.first_name} ${emp.last_name}`.toLowerCase().includes(value.toLowerCase()) ||
                    emp.emp_code.toLowerCase().includes(value.toLowerCase())
                );
                setFilteredEmployees(filtered);
                setShowEmployeeList(true);
            } else {
                setFilteredEmployees(employees);
                setShowEmployeeList(false);
            }
        }
    };

    const selectEmployee = (employee) => {
        setFormData(prev => ({
            ...prev,
            emp_id: employee.emp_id,
            employee_search: `${employee.emp_code} - ${employee.first_name} ${employee.last_name}`
        }));
        setShowEmployeeList(false);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');

        // Debug log
        console.log('Form data before submit:', formData);
        console.log('Emp ID:', formData.emp_id);

        if (editingLayoff && !userPermissions.can_edit) {
            alert('You do not have permission to edit layoff records');
            return;
        }

        if (!editingLayoff && !userPermissions.can_add) {
            alert('You do not have permission to add layoff records');
            return;
        }

        if (!formData.emp_id) {
            setError('Please select an employee');
            return;
        }

        try {
            const token = localStorage.getItem('authToken');
            const url = editingLayoff
                ? `${API_BASE}/layoff/update_layoff.php`
                : `${API_BASE}/layoff/add_layoff.php`;

            // Send only necessary fields, not employee_search
            const submitData = {
                emp_id: formData.emp_id,
                from_date: formData.from_date,
                to_date: formData.to_date,
                reason: formData.reason,
                att_value: formData.att_value
            };

            if (editingLayoff) {
                submitData.layoff_id = editingLayoff.layoff_id;
            }

            console.log('Sending data to API:', submitData);

            const response = await axios.post(url,
                submitData,
                { headers: { Authorization: `Bearer ${token}` } }
            );

            if (response.data.success) {
                fetchLayoffs();
                resetForm();
                setShowModal(false);
            } else {
                setError(response.data.error || 'Failed to save layoff record');
            }
        } catch (error) {
            console.error('API Error:', error);
            setError(error.response?.data?.error || 'Failed to save layoff record');
        }
    };

    const handleEdit = (layoff) => {
        if (!userPermissions.can_edit) {
            alert('You do not have permission to edit layoff records');
            return;
        }
        setEditingLayoff(layoff);
        setFormData({
            emp_id: layoff.emp_id,
            employee_search: `${layoff.emp_code} - ${layoff.employee_name}`,
            from_date: layoff.from_date,
            to_date: layoff.to_date,
            reason: layoff.reason,
            att_value: layoff.att_value || ''
        });
        setShowModal(true);
    };

    const handleDelete = async (layoffId) => {
        if (!userPermissions.can_delete) {
            alert('You do not have permission to delete layoff records');
            return;
        }

        if (window.confirm('Are you sure you want to delete this layoff record?')) {
            try {
                const token = localStorage.getItem('authToken');
                const response = await axios.post(`${API_BASE}/layoff/delete_layoff.php`,
                    { layoff_id: layoffId },
                    { headers: { Authorization: `Bearer ${token}` } }
                );

                if (response.data.success) {
                    fetchLayoffs();
                } else {
                    setError(response.data.error || 'Failed to delete layoff record');
                }
            } catch (error) {
                setError(error.response?.data?.error || 'Failed to delete layoff record');
            }
        }
    };

    const resetForm = () => {
        setFormData({
            emp_id: '',
            employee_search: '',
            from_date: '',
            to_date: '',
            reason: '',
            att_value: ''
        });
        setEditingLayoff(null);
        setShowEmployeeList(false);
    };

    const handleModalClose = () => {
        setShowModal(false);
        resetForm();
    };

    const handleExport = () => {
        if (!userPermissions.can_export) {
            alert('You do not have permission to export layoff records');
            return;
        }

        // Export to CSV
        const headers = ['Employee Code', 'Employee Name', 'From Date', 'To Date', 'Value', 'Reason', 'Created On'];
        const csvContent = [
            headers.join(','),
            ...layoffs.map(l => [
                l.emp_code,
                l.employee_name,
                l.from_date,
                l.to_date,
                l.att_value || '0.00',
                `"${l.reason.replace(/"/g, '""')}"`,
                l.create_on
            ].join(','))
        ].join('\n');

        const blob = new Blob([csvContent], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `layoff_records_${new Date().toISOString().split('T')[0]}.csv`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
    };

    const goToDashboard = () => navigate('/dashboard');
    const goToEmployees = () => navigate('/employees');
    const goToHome = () => navigate('/');

    if (!userPermissions.loaded) {
        return (
            <div className="d-flex justify-content-center align-items-center youtube-page-container" style={{ height: '50vh' }}>
                <div className="text-center">
                    <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </div>
                    <p className="mt-2 text-muted">Checking permissions...</p>
                </div>
            </div>
        );
    }

    if (!userPermissions.can_view) {
        return (
            <div className="container-fluid py-4 youtube-page-container">
                <div className="row">
                    <div className="col-12">
                        <div className="card shadow-sm border-0">
                            <div className="card-body text-center py-5">
                                <i className="bi bi-shield-exclamation display-1 text-warning"></i>
                                <h3 className="mt-3 text-warning">Access Denied</h3>
                                <p className="text-muted">You do not have permission to view layoff records.</p>
                                <button className="btn btn-primary mt-3" onClick={goToDashboard}>
                                    <i className="bi bi-speedometer2 me-2"></i>
                                    Back to Dashboard
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    if (loading) {
        return (
            <div className="d-flex justify-content-center align-items-center youtube-page-container" style={{ height: '50vh' }}>
                <div className="text-center">
                    <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </div>
                    <p className="mt-2 text-muted">Loading records...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="container-fluid py-4 youtube-page-container">
            {/* Header */}
            <div className="row mb-4">
                <div className="col-12">
                    <div className="card shadow-sm border-0">
                        <div className="card-body py-4">
                            <div className="row align-items-center">
                                <div className="col-md-6">
                                    <div className="d-flex align-items-center">
                                        <div className="btn-group me-3" role="group">
                                            <button className="btn btn-outline-secondary btn-sm" onClick={goToHome} title="Go to Home">
                                                <i className="bi bi-house"></i>
                                            </button>
                                            <button className="btn btn-outline-secondary btn-sm" onClick={goToDashboard} title="Go to Dashboard">
                                                <i className="bi bi-speedometer2"></i>
                                            </button>
                                            <button className="btn btn-outline-secondary btn-sm" onClick={goToEmployees} title="Go to Employees">
                                                <i className="bi bi-people"></i>
                                            </button>
                                        </div>
                                        <div>
                                            <h2 className="h4 mb-1 fw-bold text-dark">
                                                <i className="bi bi-person-x me-2 text-danger"></i>
                                                Layoff Register
                                                {!userPermissions.can_add && !userPermissions.can_edit && !userPermissions.can_delete && (
                                                    <span className="badge bg-warning ms-2">View Only</span>
                                                )}
                                            </h2>
                                            <p className="text-muted mb-0">Manage employee layoff periods and reasons</p>
                                        </div>
                                    </div>
                                </div>
                                <div className="col-md-6 text-end">
                                    <div className="btn-group" role="group">
                                        {userPermissions.can_export && (
                                            <button className="btn btn-outline-info btn-lg" onClick={handleExport} title="Export Layoff Records">
                                                <i className="bi bi-download me-2"></i>
                                                Export
                                            </button>
                                        )}
                                        {userPermissions.can_add && (
                                            <button className="btn btn-primary btn-lg" onClick={() => setShowModal(true)}>
                                                <i className="bi bi-plus-circle me-2"></i>
                                                Add Layoff Record
                                            </button>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Error Alert */}
            {error && (
                <div className="row mb-4">
                    <div className="col-12">
                        <div className="alert alert-danger d-flex align-items-center" role="alert">
                            <i className="bi bi-exclamation-triangle-fill me-2"></i>
                            <div>{error}</div>
                            <button type="button" className="btn-close ms-auto" onClick={() => setError('')}></button>
                        </div>
                    </div>
                </div>
            )}

            {/* Layoff Records Table */}
            <div className="row">
                <div className="col-12">
                    <div className="card shadow-sm border-0">
                        <div className="card-body p-0">
                            {layoffs.length === 0 ? (
                                <div className="text-center py-5">
                                    <i className="bi bi-person-x display-1 text-muted"></i>
                                    <h4 className="mt-3 text-muted">No layoff records found</h4>
                                    <p className="text-muted">Get started by adding your first layoff record.</p>
                                    {userPermissions.can_add && (
                                        <button className="btn btn-primary mt-3" onClick={() => setShowModal(true)}>
                                            <i className="bi bi-plus-circle me-2"></i>
                                            Add First Record
                                        </button>
                                    )}
                                </div>
                            ) : (
                                <div className="table-responsive">
                                    <table className="table table-hover align-middle mb-0">
                                        <thead className="bg-light">
                                            <tr>
                                                <th className="ps-4">Employee</th>
                                                <th>From Date</th>
                                                <th>To Date</th>
                                                <th>Duration</th>
                                                <th>Value</th>
                                                <th>Reason</th>
                                                <th>Created On</th>
                                                <th className="text-end pe-4">Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {layoffs.map((layoff) => {
                                                const fromDate = new Date(layoff.from_date);
                                                const toDate = new Date(layoff.to_date);
                                                const durationMs = toDate - fromDate;
                                                const durationDays = Math.floor(durationMs / (1000 * 60 * 60 * 24));

                                                return (
                                                    <tr key={layoff.layoff_id}>
                                                        <td className="ps-4">
                                                            <div className="fw-semibold">{layoff.employee_name}</div>
                                                            <small className="text-muted">Code: {layoff.emp_code}</small>
                                                        </td>
                                                        <td>
                                                            <span className="badge bg-secondary">{layoff.from_date}</span>
                                                        </td>
                                                        <td>
                                                            <span className="badge bg-secondary">{layoff.to_date}</span>
                                                        </td>
                                                        <td>
                                                            <span className="badge bg-info">{durationDays} days</span>
                                                        </td>
                                                        <td>
                                                            <span className="fw-bold">{layoff.att_value || '0.00'}</span>
                                                        </td>
                                                        <td>
                                                            <small className="text-muted">{layoff.reason.substring(0, 50)}...</small>
                                                        </td>
                                                        <td>
                                                            <small>{layoff.create_on}</small>
                                                        </td>
                                                        <td className="text-end pe-4">
                                                            <div className="btn-group" role="group">
                                                                {userPermissions.can_edit && (
                                                                    <button className="btn btn-outline-primary btn-sm" onClick={() => handleEdit(layoff)} title="Edit">
                                                                        <i className="bi bi-pencil"></i>
                                                                    </button>
                                                                )}
                                                                {userPermissions.can_delete && (
                                                                    <button className="btn btn-outline-danger btn-sm" onClick={() => handleDelete(layoff.layoff_id)} title="Delete">
                                                                        <i className="bi bi-trash"></i>
                                                                    </button>
                                                                )}
                                                            </div>
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* Stats Footer */}
            <div className="row mt-4">
                <div className="col-12">
                    <div className="card bg-light border-0">
                        <div className="card-body py-3">
                            <div className="row text-center">
                                <div className="col-md-4">
                                    <div className="fw-bold text-primary fs-4">{layoffs.length}</div>
                                    <small className="text-muted">Total Records</small>
                                </div>
                                <div className="col-md-4">
                                    <div className="fw-bold text-success fs-4">
                                        {layoffs.filter(l => new Date(l.to_date) > new Date()).length}
                                    </div>
                                    <small className="text-muted">Active Layoffs</small>
                                </div>
                                <div className="col-md-4">
                                    <div className="fw-bold text-warning fs-4">
                                        {layoffs.reduce((total, l) => {
                                            const from = new Date(l.from_date);
                                            const to = new Date(l.to_date);
                                            return total + Math.floor((to - from) / (1000 * 60 * 60 * 24));
                                        }, 0)}
                                    </div>
                                    <small className="text-muted">Total Layoff Days</small>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Quick Navigation Footer */}
            <div className="row mt-4">
                <div className="col-12">
                    <div className="card border-0">
                        <div className="card-body text-center py-3">
                            <div className="btn-group" role="group">
                                <button className="btn btn-outline-primary" onClick={goToHome}>
                                    <i className="bi bi-house me-2"></i>
                                    Back to Home
                                </button>
                                <button className="btn btn-outline-success" onClick={goToDashboard}>
                                    <i className="bi bi-speedometer2 me-2"></i>
                                    Back to Dashboard
                                </button>
                                <button className="btn btn-outline-info" onClick={goToEmployees}>
                                    <i className="bi bi-people me-2"></i>
                                    Back to Employees
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Add/Edit Modal */}
            {showModal && (
                <div className="modal fade show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
                    <div className="modal-dialog modal-dialog-centered">
                        <div className="modal-content border-0 shadow">
                            <div className="modal-header bg-primary text-white">
                                <h5 className="modal-title">
                                    <i className="bi bi-person-x me-2"></i>
                                    {editingLayoff ? 'Edit Layoff Record' : 'Add Layoff Record'}
                                </h5>
                                <button type="button" className="btn-close btn-close-white" onClick={handleModalClose}></button>
                            </div>

                            <form onSubmit={handleSubmit}>
                                <div className="modal-body">
                                    <div className="row g-3">
                                        <div className="col-12 position-relative">
                                            <label className="form-label">Employee *</label>
                                            <input
                                                type="text"
                                                className="form-control"
                                                name="employee_search"
                                                value={formData.employee_search || ''}
                                                onChange={handleInputChange}
                                                placeholder="Search by employee code or name"
                                                required
                                                autoComplete="off"
                                            />
                                            {showEmployeeList && filteredEmployees.length > 0 && (
                                                <div className="list-group position-absolute w-100" style={{ zIndex: 1050, maxHeight: '200px', overflowY: 'auto' }}>
                                                    {filteredEmployees.map(emp => (
                                                        <button
                                                            type="button"
                                                            key={emp.emp_id}
                                                            className="list-group-item list-group-item-action"
                                                            onClick={() => selectEmployee(emp)}
                                                        >
                                                            <div className="fw-semibold">{emp.emp_code} - {emp.first_name} {emp.last_name}</div>
                                                            <small className="text-muted">{emp.email}</small>
                                                        </button>
                                                    ))}
                                                </div>
                                            )}
                                        </div>

                                        <div className="col-md-4">
                                            <label className="form-label">From Date *</label>
                                            <input
                                                type="date"
                                                className="form-control"
                                                name="from_date"
                                                value={formData.from_date}
                                                onChange={handleInputChange}
                                                required
                                            />
                                        </div>

                                        <div className="col-md-4">
                                            <label className="form-label">To Date *</label>
                                            <input
                                                type="date"
                                                className="form-control"
                                                name="to_date"
                                                value={formData.to_date}
                                                onChange={handleInputChange}
                                                required
                                            />
                                        </div>

                                        <div className="col-md-4">
                                            <label className="form-label">Att. Value *</label>
                                            <input
                                                type="number"
                                                step="0.01"
                                                min="0"
                                                max="1"
                                                className="form-control"
                                                name="att_value"
                                                value={formData.att_value}
                                                onChange={handleInputChange}
                                                placeholder="0.00 to 1.00"
                                                required
                                            />
                                        </div>

                                        <div className="col-12">
                                            <label className="form-label">Reason *</label>
                                            <textarea
                                                className="form-control"
                                                name="reason"
                                                value={formData.reason}
                                                onChange={handleInputChange}
                                                rows="3"
                                                required
                                                placeholder="Enter reason for layoff"
                                            ></textarea>
                                        </div>
                                    </div>
                                </div>

                                <div className="modal-footer">
                                    <button type="button" className="btn btn-secondary" onClick={handleModalClose}>
                                        Cancel
                                    </button>
                                    <button type="submit" className="btn btn-primary">
                                        {editingLayoff ? 'Update Record' : 'Add Record'}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Layoff;